import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { PaymentElement } from '@stripe/react-stripe-js';

import { BodyText, DisplayText, theme } from '@sg/garnish';

import { type StripePaymentElementProps } from '../../types';
import { useInertPaymentElement } from './useInertPaymentElement.web';

// ────────────────────────────────────────────────────────────────────────────

export const StripePaymentElement = (props: Omit<StripePaymentElementProps, 'children'>) => {
  const { show, hasStripeFailedToLoad } = props;
  const id = useInertPaymentElement(!show);

  return (
    <View nativeID={id} style={show ? null : styles.hide} aria-hidden={!show}>
      <PaymentElement />

      {hasStripeFailedToLoad ? (
        <BodyText style={styles.failure} sizeMatch={['20']}>
          <FormattedMessage {...messages.stripeFailedToLoad} />
        </BodyText>
      ) : null}
    </View>
  );
};

const Container = (props: Pick<StripePaymentElementProps, 'children' | 'show'>) => {
  const { children, show } = props;

  return (
    <View style={[styles.container, show ? null : styles.hide]}>
      <DisplayText sizeMatch={['32']}>
        <FormattedMessage {...messages.title} />
      </DisplayText>

      {children}
    </View>
  );
};

StripePaymentElement.Container = Container;

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    padding: theme.spacing['4'],
    paddingTop: theme.spacing['20'],
    paddingBottom: theme.spacing['10'],
    gap: theme.spacing['4'],
  },
  hide: {
    maxHeight: 0,
    paddingTop: 0,
    paddingBottom: 0,
    opacity: 0,
  },
  failure: {
    color: theme.colors.CHARCOAL,
  },
});

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  title: {
    defaultMessage: 'Select payment method',
    description: 'Stripe Payment Element | Title',
  },
  stripeFailedToLoad: {
    defaultMessage:
      'Something went wrong while loading the payment provider. Please refresh the page.',
    description: 'Stripe Payment Element | Stripe failed to load',
  },
});
